import { ref, onMounted, onUnmounted, watch, readonly, type Ref } from 'vue';
import { createWatermark } from '@/utils/watermark';
import type { WatermarkConfig, WatermarkInstance } from '@/types/watermark';

/**
 * 水印组合式API
 * @param options 水印配置选项
 * @returns 水印实例和控制方法
 */
export const useWatermark = (
  options: Ref<WatermarkConfig> | WatermarkConfig
) => {
  const watermarkInstance = ref<WatermarkInstance | null>(null);
  const isVisible = ref<boolean>(true);

  // 初始化水印
  const initWatermark = () => {
    const config =
      typeof options === 'object' && 'value' in options
        ? options.value
        : options;
    watermarkInstance.value = createWatermark(config);
  };

  // 更新水印
  const updateWatermark = (newOptions: Partial<WatermarkConfig>) => {
    if (watermarkInstance.value) {
      watermarkInstance.value.update(newOptions);
    }
  };

  // 显示水印
  const showWatermark = () => {
    if (!watermarkInstance.value) {
      initWatermark();
    } else {
      watermarkInstance.value.render();
    }
    isVisible.value = true;
  };

  // 隐藏水印
  const hideWatermark = () => {
    if (watermarkInstance.value) {
      watermarkInstance.value.destroy();
      watermarkInstance.value = null;
    }
    isVisible.value = false;
  };

  // 重新渲染水印
  const renderWatermark = () => {
    if (watermarkInstance.value) {
      watermarkInstance.value.render();
    }
  };

  // 监听options变化（如果是ref）
  if (typeof options === 'object' && 'value' in options) {
    watch(
      options,
      (newOptions: WatermarkConfig) => {
        if (watermarkInstance.value && newOptions) {
          updateWatermark(newOptions);
        }
      },
      { deep: true }
    );
  }

  // 组件挂载时初始化
  onMounted(() => {
    initWatermark();
  });

  // 组件卸载时清理
  onUnmounted(() => {
    hideWatermark();
  });

  return {
    watermarkInstance: readonly(watermarkInstance),
    isVisible: readonly(isVisible),
    updateWatermark,
    showWatermark,
    hideWatermark,
    renderWatermark
  };
};
